import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, CalendarDays, Landmark, Waves } from "lucide-react";
import { Section, Overline, PrimaryButton, SecondaryButton, Card } from "../components/Shared";
import { Seo } from "../components/Seo";
import { FaqSection } from "../components/FaqSection";
import { regionBySlug, nearbyRegions, regionLabel, regionPath } from "../lib/regions";
import { regionMeta, regionSchema, regionFaq } from "../lib/seo";
import { SOLUTIONS, PROCESS } from "../lib/data";
import { regionDetails } from "../lib/regionDetails";
import { RegionOverview, RegionCities, RegionProjects, RegionServiceDetail, RegionWaterbodyTypes } from "../components/RegionContent";

const Fact = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testId}>
    <Icon className="w-5 h-5 text-cyan-700 mt-0.5 shrink-0" />
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">{label}</div>
      <div className="text-base text-slate-800 font-medium mt-0.5">{value}</div>
    </div>
  </div>
);

export default function RegionPage() {
  const { slug } = useParams();
  const region = regionBySlug(slug);
  const data = useMemo(() => {
    if (!region) return null;
    const details = regionDetails(region);
    const faq = regionFaq(region, details);
    return { details, meta: regionMeta(region, details), faq, schema: regionSchema(region, faq, details), crumbs: [["Locations", "/locations"], [region.name, regionPath(region)]], nearby: nearbyRegions(region) };
  }, [region]);

  if (!region) return <Navigate to="/locations" replace />;
  const kind = regionLabel(region);
  const quoteTo = `/request-a-quote?region=${encodeURIComponent(region.name)}`;

  return (
    <>
      <Seo meta={data.meta} jsonLd={data.schema} breadcrumbs={data.crumbs} />

      <Section className="pt-36" >
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500 flex items-center gap-2" data-testid="region-breadcrumb">
          <Link to="/" className="hover:text-cyan-700">Home</Link><span>/</span>
          <Link to="/locations" className="hover:text-cyan-700">Locations</Link><span>/</span>
          <span className="text-slate-800">{region.name}</span>
        </nav>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Overline>Lake Mapping in {region.name}</Overline>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]" data-testid="region-title">
              Lake &amp; reservoir mapping in {region.name}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-700 leading-relaxed" data-testid="region-intro">{region.intro}</p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              {region.name} has {region.lakes}. Lake Monitor AI provides on-site bathymetric depth mapping, aquatic
              vegetation mapping, bottom composition and hardness mapping, fish and habitat mapping, side-scan imagery and
              GIS deliverables for waterbodies of every size across the {kind} — from private ponds to major reservoirs.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <PrimaryButton to={quoteTo} testId="region-quote-button">
                Request a {region.abbr} survey quote <ArrowRight className="w-4 h-4" />
              </PrimaryButton>
              <SecondaryButton to="/#sample-map" testId="region-demo-button">See a sample map</SecondaryButton>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Fact icon={Waves} label="Waterbodies" value={region.lakes} testId="region-fact-lakes" />
            <Fact icon={CalendarDays} label="Survey season" value={region.season} testId="region-fact-season" />
            <Fact icon={Landmark} label="Regulatory context" value={region.agency} testId="region-fact-agency" />
            <Fact icon={MapPin} label="Coverage" value={`Entire ${kind}, we travel to you`} testId="region-fact-coverage" />
          </div>
        </div>
      </Section>

      <Section className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card className="p-8" testId="region-waterbodies">
            <Overline>Waterbodies we map</Overline>
            <h2 className="mt-3 text-2xl font-bold text-slate-900">Lakes &amp; reservoirs in {region.name}</h2>
            <p className="mt-3 text-slate-600">Well-known {region.name} waters like these — and thousands of smaller lakes and ponds — benefit from current depth, vegetation and habitat maps.</p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {region.waterbodies.map((w) => (
                <li key={w} className="flex items-center gap-2.5 text-slate-800"><CheckCircle2 className="w-5 h-5 text-cyan-600 shrink-0" />{w}</li>
              ))}
            </ul>
          </Card>
          <Card className="p-8" testId="region-challenges">
            <Overline>Regional challenges</Overline>
            <h2 className="mt-3 text-2xl font-bold text-slate-900">What {region.name} lake managers are dealing with</h2>
            <ul className="mt-5 space-y-3">
              {region.challenges.map((c) => (
                <li key={c} className="flex items-start gap-2.5 text-slate-700"><CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 shrink-0" />{c}</li>
              ))}
            </ul>
            <p className="mt-5 text-slate-600">Each of these starts with an accurate map. Our surveys give you the baseline and the year-over-year comparison to act on.</p>
          </Card>
        </div>
      </Section>

      <RegionOverview region={region} details={data.details} />
      <RegionServiceDetail region={region} />
      <RegionProjects region={region} details={data.details} />
      <RegionCities region={region} details={data.details} />
      <RegionWaterbodyTypes region={region} />

      <Section className="pt-0">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <Overline>Who we serve in {region.name}</Overline>
              <ul className="mt-5 space-y-3">
                {SOLUTIONS.map((s) => (
                  <li key={s.slug} className="flex items-start gap-2.5 text-slate-700"><CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 shrink-0" /><span><span className="font-semibold text-slate-900">{s.audience}</span> — {s.title}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <Overline>How a {region.name} survey works</Overline>
              <ol className="mt-5 space-y-3">
                {PROCESS.map((p) => (
                  <li key={p.step} className="flex items-start gap-3 text-slate-700"><span className="font-mono text-cyan-700 font-bold">{p.step}</span><span><span className="font-semibold text-slate-900">{p.title}.</span> {p.desc}</span></li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </Section>

      <FaqSection items={data.faq} title={`${region.name} survey FAQ`} />

      <Section className="pt-0">
        <div className="rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0d1a3a] to-[#0A0F1D] p-10 sm:p-14 shadow-2xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <Overline light>Ready when you are</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Map your {region.name} lake or reservoir</h2>
              <p className="mt-4 text-lg text-slate-200">Tell us where it is and what you need. We'll plan the survey around the {region.season} window and quote it with no obligation.</p>
            </div>
            <PrimaryButton to={quoteTo} tone="hero" testId="region-quote-banner-button" className="shrink-0">Request a Quote <ArrowRight className="w-4 h-4" /></PrimaryButton>
          </div>
          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Nearby service areas</div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="region-nearby">
              {data.nearby.map((r) => (
                <Link key={r.slug} to={regionPath(r)} data-testid={`nearby-${r.slug}`} className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm text-slate-200 hover:bg-white/10 hover:border-cyan-300/50 transition-colors">
                  {r.name}
                </Link>
              ))}
            </div>
            <Link to="/locations" data-testid="region-all-locations" className="mt-3 inline-flex rounded-full border border-cyan-400/40 bg-cyan-400/10 px-4 py-1.5 text-sm text-cyan-200 hover:bg-cyan-400/20 transition-colors">All locations</Link>
          </div>
        </div>
      </Section>
    </>
  );
}
